import { useState, useEffect } from 'react';

export default function Objectives() {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const objectives = [
    {
      icon: 'ri-search-line',
      title: 'رصد وتحليل الاحتياجات',
      description: 'تشخيص علمي شامل لاحتياجات الشباب في المملكة عبر أوراق عمل متخصصة وبيانات موثوقة',
      image: 'https://readdy.ai/api/search-image?query=young%20saudi%20professionals%20analyzing%20data%20charts%20graphs%20in%20modern%20workshop%20collaborative%20research%20environment%20bright%20clean%20workspace%20technology%20innovation%20focus&width=600&height=800&seq=obj-001&orientation=portrait',
      color: 'from-[#1B5E20] to-[#2E7D32]'
    },
    {
      icon: 'ri-team-line',
      title: 'تمكين الجمعيات الشبابية',
      description: 'بناء شبكة تواصل فعالة بين الجمعيات الشبابية لتبادل الخبرات والممارسات الناجحة',
      image: 'https://readdy.ai/api/search-image?query=diverse%20group%20saudi%20youth%20networking%20connecting%20handshakes%20teamwork%20collaboration%20professional%20setting%20modern%20office%20partnership%20community%20building&width=600&height=800&seq=obj-002&orientation=portrait',
      color: 'from-[#0D3D10] to-[#1B5E20]'
    },
    {
      icon: 'ri-shake-hands-line',
      title: 'بناء الشراكات الفاعلة',
      description: 'تعزيز التعاون بين القطاعات الحكومية والخاصة وغير الربحية لخدمة قضايا الشباب',
      image: 'https://readdy.ai/api/search-image?query=business%20partnership%20saudi%20professionals%20shaking%20hands%20formal%20meeting%20government%20private%20sector%20collaboration%20modern%20conference%20room%20trust%20cooperation&width=600&height=800&seq=obj-003&orientation=portrait',
      color: 'from-[#2E7D32] to-[#1B5E20]'
    }
  ];

  return (
    <section id="objectives" className="py-10 md:py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12 md:mb-20">
          <div className="inline-flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#1B5E20] flex items-center justify-center">
              <i className="ri-target-line text-xl md:text-2xl text-white"></i>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B5E20]">الأهداف الاستراتيجية</h2>
          </div>
          <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            نسعى لبناء منصة تشاركية وطنية تُسهم في تحديد أولويات واحتياجات الشباب وصياغة حلول عملية
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {objectives.map((obj, index) => (
            <div
              key={index}
              className="group relative overflow-hidden rounded-2xl md:rounded-3xl cursor-pointer transition-all duration-500"
              style={{ aspectRatio: '3/4' }}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="absolute inset-0">
                <img
                  src={obj.image}
                  alt={obj.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${obj.color} opacity-80 group-hover:opacity-90 transition-opacity duration-500`}></div>
              </div>

              <div className="relative h-full flex flex-col justify-between p-6 md:p-10">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl md:rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                  <i className={`${obj.icon} text-4xl md:text-5xl text-white`}></i>
                </div>

                <div className="space-y-3 md:space-y-4">
                  <h3 className="text-2xl md:text-3xl font-bold text-white leading-tight">
                    {obj.title}
                  </h3>
                  <p className={`text-white/90 text-sm md:text-base leading-relaxed transition-all duration-500 ${
                    isMobile || hoveredCard === index ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}>
                    {obj.description}
                  </p>
                </div>
              </div>

              <div className={`absolute bottom-0 right-0 left-0 h-2 bg-[#D4AF37] transition-all duration-500 ${
                isMobile || hoveredCard === index ? 'opacity-100' : 'opacity-0'
              }`}></div>
            </div>
          ))}
        </div>

        <div className="mt-16 md:mt-24 bg-white rounded-2xl md:rounded-3xl p-6 md:p-12 shadow-lg">
          <h3 className="text-2xl md:text-3xl font-bold text-[#1B5E20] mb-6 md:mb-8 text-center">علاقة المشروع برؤية المملكة 2030</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="text-center space-y-3 md:space-y-4">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#1B5E20]/10 flex items-center justify-center mx-auto">
                <i className="ri-heart-pulse-line text-2xl md:text-3xl text-[#1B5E20]"></i>
              </div>
              <h4 className="text-lg md:text-xl font-bold text-gray-800">مجتمع حيوي</h4>
              <p className="text-gray-600 text-sm">تمكين الشباب ورفع جودة حياتهم</p>
            </div>
            <div className="text-center space-y-3 md:space-y-4">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#1B5E20]/10 flex items-center justify-center mx-auto">
                <i className="ri-line-chart-line text-2xl md:text-3xl text-[#1B5E20]"></i>
              </div>
              <h4 className="text-lg md:text-xl font-bold text-gray-800">اقتصاد مزدهر</h4>
              <p className="text-gray-600 text-sm">تنمية رأس المال البشري والمهارات</p>
            </div>
            <div className="text-center space-y-3 md:space-y-4">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#1B5E20]/10 flex items-center justify-center mx-auto">
                <i className="ri-flag-line text-2xl md:text-3xl text-[#1B5E20]"></i>
              </div>
              <h4 className="text-lg md:text-xl font-bold text-gray-800">وطن طموح</h4>
              <p className="text-gray-600 text-sm">تعزيز المشاركة المجتمعية وصناعة القرار</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
